import {type MatchParams} from '@wandb/weave/common/types/base';
import {useMemo} from 'react';
import {type match, matchPath, useLocation} from 'react-router';

import {AUTOMATIONS_PATH} from '../../../routes/automations/automationsPaths';
import {REGISTRY_PATHS} from '../../../routes/registry/registryPaths';
import {
  PROFILE_BASE_PATH,
  PROFILE_BASE_PROJECTS_PATH,
  PROFILE_PATH,
  PROFILE_PROJECTS_PATH,
  TEAM_MEMBERS_PATH,
  TEAM_SERVICE_ACCOUNTS_PATH,
} from '../../../routes/team/teamPaths';
import {useCurrentRouteMatch} from '../../../routes/utils';
import {
  PROJECT_BASE_PATH,
  PROJECT_RUNS_TABLE_PATH,
  PROJECT_WORKSPACE_PATH,
} from '../../../routes/workspace/multiRunWorkspacePaths';
import * as suggestions from './catalog';
import {type AgentSuggestion} from './catalog';

/**
 * useActiveSuggestions returns the suggestion set for the current route,
 * recomputed on navigation. Must be called within the app router.
 */
export function useActiveSuggestions(): AgentSuggestion[] {
  const routeMatch = useCurrentRouteMatch();
  const {pathname} = useLocation();
  return useMemo(
    () => resolveSuggestions(routeMatch, pathname),
    [routeMatch, pathname]
  );
}

/**
 * resolveSuggestions maps a resolved route to its suggestion set. It reads the
 * route the app already matched; the only pathname re-match is workspace vs
 * runs table, which the router cannot distinguish because
 * `/:entity/:project/table` is unregistered and swallowed by the project base.
 */
export function resolveSuggestions(
  routeMatch: match | null,
  pathname: string
): AgentSuggestion[] {
  if (routeMatch == null) {
    return DEFAULT;
  }

  const params: SuggestionRouteParams = routeMatch.params;

  // Gate runs/weave on `tab` so every sub-route is caught, not just the one
  // whose pattern equals match.path (e.g. the Files tab resolves to its own
  // route).
  if (params.tab === 'runs') {
    return params.runTab === 'logs' ? RUN_LOGS : RUN;
  }
  if (params.tab === 'weave') {
    if (params.weaveTab == null) {
      return DEFAULT;
    }
    if (params.weaveTab === 'evaluations') {
      return WEAVE_EVALS;
    }
    if (WEAVE_SIGNAL_TABS.has(params.weaveTab)) {
      return WEAVE_SIGNALS;
    }
    if (WEAVE_CONVERSATION_TABS.has(params.weaveTab)) {
      return WEAVE_CONVERSATIONS;
    }

    return DEFAULT;
  }

  const mapped = PATH_SUGGESTIONS[routeMatch.path];
  if (mapped != null) {
    return mapped;
  }

  if (REGISTRY_PATHS.includes(routeMatch.path)) {
    return REGISTRY;
  }

  // matchPath below tests the raw pathname by shape, and `/:entity/:project`
  // matches any two-segment URL. Gate on projectName so a non-project page of
  // that shape is not mistaken for a workspace, runs table, or automations page.
  if (params.projectName != null) {
    if (matchPath(pathname, {path: PROJECT_RUNS_TABLE_PATH, exact: true})) {
      return RUNS_TABLE;
    }
    if (
      matchPath(pathname, {
        path: [PROJECT_BASE_PATH, PROJECT_WORKSPACE_PATH],
        exact: true,
      })
    ) {
      return WORKSPACE;
    }
    if (matchPath(pathname, {path: PROJECT_AUTOMATIONS_PATH, exact: true})) {
      return AUTOMATIONS;
    }
  }

  return DEFAULT;
}

type SuggestionRouteParams = Pick<MatchParams, 'projectName' | 'tab'> & {
  runTab?: string;
  weaveTab?: string;
};

const DEFAULT = [
  suggestions.recentCrashedRuns,
  suggestions.starredReports,
  suggestions.recentlyFinishedRuns,
  suggestions.whatCanYouDo,
];

const WORKSPACE = [
  suggestions.summarizeReport,
  suggestions.recommendNextExperiment,
  suggestions.patterns,
  suggestions.roastMyProject,
];
const RUNS_TABLE = [
  suggestions.summarizeReport,
  suggestions.setBaseline,
  suggestions.patterns,
  suggestions.recommendNextExperiment,
];
const RUN_LOGS = [
  suggestions.summarizeReport,
  suggestions.analyzeLogs,
  suggestions.patterns,
  suggestions.recommendNextExperiment,
];
const RUN = [
  suggestions.summarizeReport,
  suggestions.compareRecentRuns,
  suggestions.patterns,
  suggestions.recommendNextExperiment,
];
const NON_PROJECT = [
  suggestions.crashedRuns,
  suggestions.activeRuns,
  suggestions.starProjects,
  suggestions.whatCanYouDo,
];
const REGISTRY = [
  suggestions.collectionsActivity,
  suggestions.usefulRegistries,
  suggestions.cleanupRegistries,
  suggestions.registriesWithAutomations,
];
const AUTOMATIONS = [
  suggestions.understandAutomations,
  suggestions.createAutomation,
  suggestions.describeAutomations,
  suggestions.configureGithubAction,
];
const WEAVE_CONVERSATIONS = [
  suggestions.frustratedUsers,
  suggestions.summarizeConversations,
  suggestions.patterns,
  suggestions.roastMyProject,
];
const WEAVE_SIGNALS = [
  suggestions.createSignal,
  suggestions.signalsFound,
  suggestions.frustratedUsers,
  suggestions.roastMyProject,
];
const WEAVE_EVALS = [
  suggestions.createDataset,
  suggestions.createScorers,
  suggestions.evalImproved,
  suggestions.whatToTryNext,
];

const PROJECT_AUTOMATIONS_PATH = `${PROJECT_BASE_PATH}/automations`;

// Product names for these weave surfaces are still in flux, so each set matches
// both candidate `weaveTab` values.
const WEAVE_CONVERSATION_TABS = new Set(['agents', 'traces', 'calls']);
const WEAVE_SIGNAL_TABS = new Set(['monitors', 'signals']);

// Keyed by the resolved route pattern (match.path), not the raw URL, so the
// app's routing already decided which pattern applies. Patterns not listed
// here fall through to default.
const PATH_SUGGESTIONS: Record<string, AgentSuggestion[]> = {
  [PROFILE_PATH]: NON_PROJECT,
  [PROFILE_PROJECTS_PATH]: NON_PROJECT,
  [TEAM_MEMBERS_PATH]: NON_PROJECT,
  [TEAM_SERVICE_ACCOUNTS_PATH]: NON_PROJECT,
  [PROFILE_BASE_PATH]: NON_PROJECT,
  [PROFILE_BASE_PROJECTS_PATH]: NON_PROJECT,
  [AUTOMATIONS_PATH]: AUTOMATIONS,
};

// Every suggestion group, keyed by family. resolveSuggestions returns one of
// these; the unit test iterates them to guard per-group invariants.
export const SUGGESTION_GROUPS = {
  default: DEFAULT,
  workspace: WORKSPACE,
  runsTable: RUNS_TABLE,
  run: RUN,
  runLogs: RUN_LOGS,
  nonProject: NON_PROJECT,
  registry: REGISTRY,
  automations: AUTOMATIONS,
  weaveConversations: WEAVE_CONVERSATIONS,
  weaveSignals: WEAVE_SIGNALS,
  weaveEvals: WEAVE_EVALS,
};
